import { useState } from "react";
import { Users, Maximize2, Download, Copy, Check, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

type Competitor = {
  name: string;
  type: string;
  strategy: string;
  strength: string;
  weakness: string;
};

export type CompetitorsAnalysis = {
  market_summary: string;
  competitors: Competitor[];
  market_gaps: string[];
  positioning_advice: string;
  generated_at?: string;
};

export function CompetitorOverlay({
  data,
  trendTitle,
}: {
  data: CompetitorsAnalysis;
  trendTitle?: string;
}) {
  if (!data?.competitors?.length) return null;
  return (
    <div className="mt-3 rounded-lg border border-border/60 bg-secondary/20 p-3 text-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground">
          <Users className="size-3 text-primary" /> competitor overlay
        </div>
        <ExpandedDialog data={data} trendTitle={trendTitle}>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] font-mono">
            <Maximize2 className="size-3" /> expand
          </Button>
        </ExpandedDialog>
      </div>
      <p className="text-muted-foreground text-xs mb-3">{data.market_summary}</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        {data.competitors.slice(0, 4).map((c, i) => (
          <div key={i} className="rounded border border-border/60 p-2">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="font-semibold text-xs">{c.name}</div>
              <span className="font-mono text-[10px] text-muted-foreground">{c.type}</span>
            </div>
            <div className="text-xs text-muted-foreground">{c.strategy}</div>
            <div className="text-[11px] mt-1">
              <span className="text-primary">+ {c.strength}</span>
            </div>
            <div className="text-[11px]">
              <span className="text-destructive">– {c.weakness}</span>
            </div>
          </div>
        ))}
      </div>
      {data.market_gaps?.length > 0 && (
        <div className="mb-2">
          <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
            market gaps
          </div>
          <ul className="list-disc list-inside text-xs text-muted-foreground space-y-0.5">
            {data.market_gaps.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </div>
      )}
      {data.positioning_advice && (
        <div className="text-xs">
          <b>Positioning:</b>{" "}
          <span className="text-muted-foreground">{data.positioning_advice}</span>
        </div>
      )}
    </div>
  );
}

function buildMarkdown(data: CompetitorsAnalysis, trendTitle?: string) {
  const lines: string[] = [];
  lines.push(`# Competitor Snapshot${trendTitle ? `: ${trendTitle}` : ""}`);
  if (data.generated_at) lines.push(`_Generated ${new Date(data.generated_at).toLocaleString()}_`);
  lines.push("", "## Market summary", data.market_summary, "", "## Competitors");
  data.competitors.forEach((c) => {
    lines.push(
      `### ${c.name} _(${c.type})_`,
      `- **Strategy:** ${c.strategy}`,
      `- **Strength:** ${c.strength}`,
      `- **Weakness:** ${c.weakness}`,
      "",
    );
  });
  if (data.market_gaps?.length) {
    lines.push("## Market gaps");
    data.market_gaps.forEach((g) => lines.push(`- ${g}`));
    lines.push("");
  }
  if (data.positioning_advice) {
    lines.push("## Positioning advice", data.positioning_advice);
  }
  return lines.join("\n");
}

function ExpandedDialog({
  data,
  trendTitle,
  children,
}: {
  data: CompetitorsAnalysis;
  trendTitle?: string;
  children: React.ReactNode;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [orientation, setOrientation] = useState<"portrait" | "landscape">("landscape");
  const [fontScale, setFontScale] = useState<"sm" | "md" | "lg">("md");
  const [sections, setSections] = useState({
    summary: true,
    table: true,
    gaps: true,
    positioning: true,
  });

  const md = buildMarkdown(data, trendTitle);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      toast({ title: "Copied", description: "Shareable summary copied to clipboard." });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const slug =
    ((trendTitle ?? "competitor-snapshot")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 60)) || "competitor-snapshot";

  const handleDownload = () => {
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${slug}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handlePdf = () => {
    const scale = fontScale === "sm" ? 0.85 : fontScale === "lg" ? 1.2 : 1;
    const fs = {
      title: 18 * scale,
      subtitle: 12 * scale,
      meta: 9 * scale,
      heading: 11 * scale,
      body: 10 * scale,
      table: 9 * scale,
    };
    const doc = new jsPDF({ orientation, unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = margin;

    const ensureSpace = (needed: number) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(fs.title);
    doc.text("Competitor Snapshot", margin, y);
    y += fs.title + 6;

    if (trendTitle) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fs.subtitle);
      doc.setTextColor(80);
      const t = doc.splitTextToSize(trendTitle, pageWidth - margin * 2);
      doc.text(t, margin, y);
      y += t.length * (fs.subtitle + 2) + 4;
    }
    if (data.generated_at) {
      doc.setFontSize(fs.meta);
      doc.setTextColor(140);
      doc.text(`Generated ${new Date(data.generated_at).toLocaleString()}`, margin, y);
      y += fs.meta + 6;
    }

    doc.setTextColor(40);

    if (sections.summary) {
      doc.setFontSize(fs.heading);
      doc.setFont("helvetica", "bold");
      doc.text("Market summary", margin, y);
      y += fs.heading + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fs.body);
      const summary = doc.splitTextToSize(data.market_summary, pageWidth - margin * 2);
      doc.text(summary, margin, y);
      y += summary.length * (fs.body + 2) + 10;
    }

    if (sections.table) {
      autoTable(doc, {
        startY: y,
        head: [["Attribute", ...data.competitors.map((c) => c.name)]],
        body: [
          ["Type", ...data.competitors.map((c) => c.type)],
          ["Strategy", ...data.competitors.map((c) => c.strategy)],
          ["Strength", ...data.competitors.map((c) => c.strength)],
          ["Weakness", ...data.competitors.map((c) => c.weakness)],
        ],
        styles: { fontSize: fs.table, cellPadding: 6, valign: "top" },
        headStyles: { fillColor: [30, 30, 40], textColor: 255 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 70, fillColor: [245, 245, 248] } },
        margin: { left: margin, right: margin },
      });
      // @ts-expect-error autotable augments doc with lastAutoTable
      y = (doc.lastAutoTable?.finalY ?? y) + 20;
    }

    if (sections.gaps && data.market_gaps?.length) {
      ensureSpace(fs.heading + 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(fs.heading);
      doc.text("Market gaps", margin, y);
      y += fs.heading + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fs.body);
      data.market_gaps.forEach((g) => {
        const lines = doc.splitTextToSize(`• ${g}`, pageWidth - margin * 2);
        ensureSpace(lines.length * (fs.body + 2) + 2);
        doc.text(lines, margin, y);
        y += lines.length * (fs.body + 2) + 2;
      });
      y += 8;
    }

    if (sections.positioning && data.positioning_advice) {
      const adv = doc.splitTextToSize(data.positioning_advice, pageWidth - margin * 2);
      ensureSpace(fs.heading + adv.length * (fs.body + 2) + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(fs.heading);
      doc.text("Positioning advice", margin, y);
      y += fs.heading + 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fs.body);
      doc.text(adv, margin, y);
    }

    doc.save(`${slug}.pdf`);
    toast({ title: "PDF exported", description: "Ready to drop into your deck." });
  };


  // Build comparison rows
  const fields: { key: keyof Competitor; label: string }[] = [
    { key: "type", label: "Type" },
    { key: "strategy", label: "Strategy" },
    { key: "strength", label: "Strength" },
    { key: "weakness", label: "Weakness" },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="size-4 text-primary" />
            Competitor snapshot{trendTitle ? ` · ${trendTitle}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={handleCopy}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? "Copied" : "Copy summary"}
          </Button>
          <Button size="sm" variant="outline" onClick={handleDownload}>
            <Download className="size-4" /> Export .md
          </Button>
          <Button size="sm" onClick={handlePdf}>
            <FileText className="size-4" /> Export PDF
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-3">
          <div className="font-mono text-[10px] uppercase text-muted-foreground">
            PDF options
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <div className="text-[11px] font-medium mb-1.5">Orientation</div>
              <div className="inline-flex rounded-md border border-border overflow-hidden">
                {(["portrait", "landscape"] as const).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOrientation(o)}
                    className={`px-3 py-1 text-xs capitalize transition ${
                      orientation === o
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium mb-1.5">Font size</div>
              <div className="inline-flex rounded-md border border-border overflow-hidden">
                {(["sm", "md", "lg"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFontScale(s)}
                    className={`px-3 py-1 text-xs uppercase transition ${
                      fontScale === s
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-medium mb-1.5">Sections</div>
              <div className="flex flex-wrap gap-3">
                {([
                  ["summary", "Summary"],
                  ["table", "Comparison"],
                  ["gaps", "Gaps"],
                  ["positioning", "Positioning"],
                ] as const).map(([key, label]) => (
                  <label key={key} className="flex items-center gap-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sections[key]}
                      onChange={(e) =>
                        setSections((s) => ({ ...s, [key]: e.target.checked }))
                      }
                      className="size-3.5 accent-primary"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">{data.market_summary}</p>

        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-secondary/40">
                <th className="text-left p-2 font-mono text-[10px] uppercase text-muted-foreground border-b border-border w-32 sticky left-0 bg-secondary/40">
                  Attribute
                </th>
                {data.competitors.map((c, i) => (
                  <th
                    key={i}
                    className="text-left p-2 font-semibold border-b border-l border-border min-w-[180px]"
                  >
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map((f) => (
                <tr key={f.key} className="align-top">
                  <td className="p-2 font-mono text-[10px] uppercase text-muted-foreground border-b border-border sticky left-0 bg-background">
                    {f.label}
                  </td>
                  {data.competitors.map((c, i) => (
                    <td
                      key={i}
                      className={`p-2 text-xs border-b border-l border-border ${
                        f.key === "strength"
                          ? "text-primary"
                          : f.key === "weakness"
                            ? "text-destructive"
                            : "text-foreground"
                      }`}
                    >
                      {c[f.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.market_gaps?.length > 0 && (
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">
              Market gaps
            </div>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-0.5">
              {data.market_gaps.map((g, i) => (
                <li key={i}>{g}</li>
              ))}
            </ul>
          </div>
        )}

        {data.positioning_advice && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
            <div className="font-mono text-[10px] uppercase text-primary mb-1">
              Positioning advice
            </div>
            {data.positioning_advice}
          </div>
        )}

        {data.generated_at && (
          <div className="font-mono text-[10px] text-muted-foreground">
            Generated {new Date(data.generated_at).toLocaleString()}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
